import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../../components/ui/Card';
type AccessRole = 'fornitore' | 'manutenzione';
type FilterType = 'all' | 'fornitore' | 'manutenzione';
type AccessLog = {
  id: string;
  name: string;
  role: AccessRole;
  date: string;
  entryTime: string;
  exitTime: string | null;
  avatar?: string;
};
// Mock data grouped by date
const mockAccessLogs: {
  date: string;
  dateLabel: string;
  logs: AccessLog[];
}[] = [{
  date: '2025-01-28',
  dateLabel: 'Oggi - 28 Gennaio',
  logs: [{
    id: '1',
    name: 'Mario Rossi',
    role: 'fornitore',
    date: '2025-01-28',
    entryTime: '09:15',
    exitTime: '11:45'
  }]
}, {
  date: '2025-01-27',
  dateLabel: 'Ieri - 27 Gennaio',
  logs: [{
    id: '2',
    name: 'Giuseppe Verdi',
    role: 'manutenzione',
    date: '2025-01-27',
    entryTime: '14:30',
    exitTime: '15:15'
  }, {
    id: '3',
    name: 'Laura Bianchi',
    role: 'fornitore',
    date: '2025-01-27',
    entryTime: '08:00',
    exitTime: '10:30'
  }]
}, {
  date: '2025-01-25',
  dateLabel: '25 Gennaio',
  logs: [{
    id: '4',
    name: 'Marco Neri',
    role: 'manutenzione',
    date: '2025-01-25',
    entryTime: '16:00',
    exitTime: '16:45'
  }]
}, {
  date: '2025-01-24',
  dateLabel: '24 Gennaio',
  logs: [{
    id: '5',
    name: 'Anna Russo',
    role: 'fornitore',
    date: '2025-01-24',
    entryTime: '09:00',
    exitTime: '11:00'
  }]
}];
function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
}
function calculateDuration(entry: string, exit: string | null): string {
  if (!exit) return '';
  const [entryH, entryM] = entry.split(':').map(Number);
  const [exitH, exitM] = exit.split(':').map(Number);
  const totalMinutes = exitH * 60 + exitM - (entryH * 60 + entryM);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}min`;
}
export function AccessHistoryPage() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedMonth, setSelectedMonth] = useState('Gennaio 2025');
  const filters: {
    id: FilterType;
    label: string;
  }[] = [{
    id: 'all',
    label: t.host.accessHistory.filterAll
  }, {
    id: 'fornitore',
    label: t.host.accessHistory.filterCleaning
  }, {
    id: 'manutenzione',
    label: t.host.accessHistory.filterMaintenance
  }];
  const filteredLogs = mockAccessLogs.map(group => ({
    ...group,
    logs: group.logs.filter(log => filter === 'all' || log.role === filter)
  })).filter(group => group.logs.length > 0);
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-917">
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-918">
        {/* Header */}
        <motion.div className="flex items-center justify-between mb-8" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-919">
          <div className="flex items-center gap-4" data-id="element-920">
            <button onClick={() => navigate('/host')} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-921">
              <ArrowLeft className="w-6 h-6 text-gray-600" data-id="element-922" />
            </button>
            <h1 className="text-2xl font-bold text-navy" data-id="element-923">
              {t.host.accessHistory.title}
            </h1>
          </div>

          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-924">
            {selectedMonth}
            <ChevronDown className="w-4 h-4" data-id="element-925" />
          </button>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-xl" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} data-id="element-926">
          {filters.map(f => <button key={f.id} onClick={() => setFilter(f.id)} className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${filter === f.id ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-927">
              {f.label}
            </button>)}
        </motion.div>

        {/* Access Logs */}
        <AnimatePresence mode="wait" data-id="element-928">
          {filteredLogs.length > 0 ? <motion.div key={filter} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="space-y-6" data-id="element-929">
              {filteredLogs.map((group, groupIndex) => <motion.div key={group.date} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: groupIndex * 0.1
          }} data-id="element-930">
                  {/* Date Header */}
                  <h3 className="text-sm font-semibold text-gray-500 mb-3" data-id="element-931">
                    {group.dateLabel}
                  </h3>

                  {/* Access Cards */}
                  <div className="space-y-3" data-id="element-932">
                    {group.logs.map(log => <Card key={log.id} className="p-4" data-id="element-933">
                        <div className="flex items-center gap-4" data-id="element-934">
                          {/* Avatar */}
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-935">
                            <span className="text-sm font-semibold text-gray-600" data-id="element-936">
                              {getInitials(log.name)}
                            </span>
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0" data-id="element-937">
                            <div className="flex items-center gap-2 mb-1" data-id="element-938">
                              <span className="font-semibold text-gray-900" data-id="element-939">
                                {log.name}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.role === 'fornitore' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-940">
                                {t.host.accessHistory.roles[log.role]}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-941">
                              <span data-id="element-942">
                                {t.host.accessHistory.entry}: {log.entryTime}
                              </span>
                              <span data-id="element-943">
                                {t.host.accessHistory.exit}:{' '}
                                {log.exitTime || <span className="text-green-600 font-medium" data-id="element-944">
                                    {t.host.accessHistory.inProgress}
                                  </span>}
                              </span>
                            </div>
                          </div>

                          {/* Duration */}
                          {log.exitTime && <div className="text-right" data-id="element-945">
                              <span className="text-sm text-gray-500" data-id="element-946">
                                {t.host.accessHistory.duration}
                              </span>
                              <p className="font-semibold text-navy" data-id="element-947">
                                {calculateDuration(log.entryTime, log.exitTime)}
                              </p>
                            </div>}
                        </div>
                      </Card>)}
                  </div>
                </motion.div>)}
            </motion.div> : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-center py-16" data-id="element-948">
              <p className="text-gray-500" data-id="element-949">{t.host.accessHistory.noAccess}</p>
            </motion.div>}
        </AnimatePresence>
      </main>
    </div>;
}